import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation} from '@angular/core';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { HcModal, HcSort, HcTableDataSource, ModalOptions, ModalService, PaginationComponent, SimpleModalOptions } from '@healthcatalyst/cashmere';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SLAElement, SLAService } from 'src/app/shared/services/sla.service';

/** Admin list of slas to edit */
@Component({
    selector: 'pd-admin-slas',
    templateUrl: './admin-slas.component.html',
    styleUrls: ['./admin-slas.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminSLAsComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<SLAElement> = new HcTableDataSource();
    loading = true;
    loadError = false;
    adminSLAsDisplayedColumns: string[] = ['id', 'name','description','actions'];
    adminSLAsPageSize: number = 10;
    private modalRef: HcModal<TemplateRef<any>>;
    adminSLAControl = new FormGroup({
        id: new FormControl(0, {nonNullable: true}), 
        name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        description: new FormControl('', {nonNullable: true})
    });
    quillConfig = {
        toolbar: [['bold', 'italic', 'underline', 'strike'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean'], ['link']]
    };
    quillStyles = {
        'font-family': "'Noto Sans', 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif"
    };


    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get adminSLAsListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor( private SLAService: SLAService, private modalService: ModalService ) {}

    ngOnInit(): void {
        this.SLAService.slaListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.dataSource = new HcTableDataSource(list);
                if ( this.sort && this.paginator ) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                }
            } else {
                this.loadError = this.SLAService.loadFailed;
                if ( !this.loadError ) {
                    this.loadSLAs();
                } else {
                    this.loading = false;
                }
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    loadSLAs(): void {
        this.SLAService.refresh();
    }

    applyFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    openAddEditModal(content: TemplateRef<any>, edit: boolean, sla?: SLAElement) {
        const options: ModalOptions = {
            size: 'md',
            data: {
                sla: sla,
                edit: edit
            }
        };
        this.adminSLAControl.reset();

        if(sla) {
            this.adminSLAControl.patchValue({
                id: sla.slaid,
                name: sla.name,
                description: sla.description      
             });
        } 
        this.modalRef = this.modalService.open(content, options);
    }


    editSLA() {
        const target: SLAElement = {
            slaid: this.adminSLAControl.controls['id'].value,
            name: this.adminSLAControl.controls['name'].value,  
            description: this.adminSLAControl.controls['description'].value
        };
        this.SLAService.update( target );
        this.modalRef.close();
    }

    addSLA() {
        const target: SLAElement = {
            slaid: -1,
            name: this.adminSLAControl.controls['name'].value,
            description: this.adminSLAControl.controls['description'].value
        };
        this.SLAService.add( target );
        this.modalRef.close();
    }

    removeSLA( element: SLAElement ) {
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the SLA: <strong>' + element.name + '</strong>?<p>',
            headerText: 'Delete SLA',
            confirmButtonText: 'Delete SLA',
            icon: ''
        };
        this.modalService.confirmDestructive(options)
            .pipe(take(1),filter(confirmed => confirmed))
            .subscribe(() => {
                this.SLAService.remove( element );
            });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}